import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Building2, Pencil, Plus, Trash2, Users } from "lucide-react";
import {
  InitialTenantUserInput,
  SuperAdminService,
  TenantOverview,
} from "../../services/SuperAdminService";
import { UserService } from "../../services/UserService";
import EditTenantModal from "../../components/super-admin/EditTenantModal";
import {
  TENANT_SLUG_REGEX,
  buildTenantPath,
  extractTenantSlugFromPathname,
  isReservedTenantSlug,
  normalizeTenantSlug,
} from "../../utils/tenantSlug";

type EmailLookupState = "idle" | "checking" | "exists" | "not_found";

interface AssignmentFormRow extends InitialTenantUserInput {
  id: string;
  emailLookupState: EmailLookupState;
  existingUserDisplayName?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeEmail = (value: string) => value.trim().toLowerCase();

const createEmptyAssignment = (): AssignmentFormRow => ({
  id: Math.random().toString(36).slice(2),
  userDisplayName: "",
  userEmail: "",
  userRole: "admin",
  password: "",
  emailLookupState: "idle",
  existingUserDisplayName: undefined,
});

const SuperAdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantOverview[]>([]);
  const [tenantBeingEdited, setTenantBeingEdited] = useState<TenantOverview | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [isSlugTouched, setIsSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "invalid">("idle");
  const [slugMessage, setSlugMessage] = useState("");
  const [assignments, setAssignments] = useState<AssignmentFormRow[]>([createEmptyAssignment()]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailLookupTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const emailLookupVersionRef = useRef<Record<string, number>>({});

  const loadTenants = async () => {
    try {
      setIsLoadingTenants(true);
      const data = await SuperAdminService.listTenants();
      setTenants(data);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar tenants.");
    } finally {
      setIsLoadingTenants(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(emailLookupTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (isSlugTouched) return;
    setTenantSlug(normalizeTenantSlug(tenantName));
  }, [isSlugTouched, tenantName]);

  const clearEmailLookupTimer = (rowId: string) => {
    const timer = emailLookupTimersRef.current[rowId];
    if (timer) {
      clearTimeout(timer);
      delete emailLookupTimersRef.current[rowId];
    }
  };

  const invalidateLookupVersion = (rowId: string) => {
    emailLookupVersionRef.current[rowId] = (emailLookupVersionRef.current[rowId] ?? 0) + 1;
  };

  const updateAssignment = (
    id: string,
    updater: (current: AssignmentFormRow) => AssignmentFormRow
  ) => {
    setAssignments((previous) =>
      previous.map((row) => (row.id === id ? updater(row) : row))
    );
  };

  const lookupUserByEmail = async (rowId: string, rawEmail: string): Promise<boolean | null> => {
    const normalizedEmail = normalizeEmail(rawEmail);

    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      updateAssignment(rowId, (row) => {
        const shouldClearName = row.emailLookupState === "exists";
        return {
          ...row,
          userEmail: rawEmail,
          emailLookupState: "idle",
          existingUserDisplayName: undefined,
          userDisplayName: shouldClearName ? "" : row.userDisplayName,
          password: shouldClearName ? "" : row.password,
        };
      });
      return null;
    }

    const requestVersion = (emailLookupVersionRef.current[rowId] ?? 0) + 1;
    emailLookupVersionRef.current[rowId] = requestVersion;

    updateAssignment(rowId, (row) => ({
      ...row,
      userEmail: normalizedEmail,
      emailLookupState: "checking",
      existingUserDisplayName: undefined,
    }));

    try {
      const existingUser = await UserService.getUserByEmail(normalizedEmail);
      if (emailLookupVersionRef.current[rowId] !== requestVersion) {
        return null;
      }

      if (existingUser) {
        const resolvedName =
          existingUser.userDisplayName?.trim() ||
          existingUser.userEmail ||
          normalizedEmail.split("@")[0];

        updateAssignment(rowId, (row) => ({
          ...row,
          userEmail: normalizedEmail,
          userDisplayName: resolvedName,
          password: "",
          emailLookupState: "exists",
          existingUserDisplayName: resolvedName,
        }));

        return true;
      }

      updateAssignment(rowId, (row) => {
        const shouldClearName = row.emailLookupState === "exists";
        return {
          ...row,
          userEmail: normalizedEmail,
          userDisplayName: shouldClearName ? "" : row.userDisplayName,
          password: shouldClearName ? "" : row.password,
          emailLookupState: "not_found",
          existingUserDisplayName: undefined,
        };
      });

      return false;
    } catch (error) {
      console.error("Erro ao validar email:", error);
      updateAssignment(rowId, (row) => ({
        ...row,
        emailLookupState: "idle",
        existingUserDisplayName: undefined,
      }));
      return null;
    }
  };

  const scheduleEmailLookup = (rowId: string, email: string) => {
    clearEmailLookupTimer(rowId);

    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      updateAssignment(rowId, (row) => {
        const shouldClearName = row.emailLookupState === "exists";
        return {
          ...row,
          userEmail: email,
          emailLookupState: "idle",
          existingUserDisplayName: undefined,
          userDisplayName: shouldClearName ? "" : row.userDisplayName,
          password: shouldClearName ? "" : row.password,
        };
      });
      return;
    }

    updateAssignment(rowId, (row) => ({
      ...row,
      userEmail: email,
      emailLookupState: "checking",
      existingUserDisplayName: undefined,
    }));

    emailLookupTimersRef.current[rowId] = setTimeout(() => {
      void lookupUserByEmail(rowId, email);
    }, 450);
  };

  const addAssignment = () => {
    setAssignments((previous) => [...previous, createEmptyAssignment()]);
  };

  const removeAssignment = (id: string) => {
    clearEmailLookupTimer(id);
    invalidateLookupVersion(id);
    setAssignments((previous) => previous.filter((row) => row.id !== id));
  };

  const resetForm = () => {
    Object.keys(emailLookupTimersRef.current).forEach(clearEmailLookupTimer);
    emailLookupVersionRef.current = {};
    setTenantName("");
    setTenantSlug("");
    setIsSlugTouched(false);
    setSlugStatus("idle");
    setSlugMessage("");
    setAssignments([createEmptyAssignment()]);
  };

  const validateSlug = async (): Promise<string | null> => {
    const normalizedSlug = normalizeTenantSlug(tenantSlug || tenantName);
    setTenantSlug(normalizedSlug);

    if (!normalizedSlug) {
      setSlugStatus("invalid");
      setSlugMessage("Slug do tenant é obrigatório.");
      return null;
    }

    if (!TENANT_SLUG_REGEX.test(normalizedSlug)) {
      setSlugStatus("invalid");
      setSlugMessage("Slug inválido: use apenas letras minúsculas, números e hífen.");
      return null;
    }

    if (isReservedTenantSlug(normalizedSlug)) {
      setSlugStatus("invalid");
      setSlugMessage("Slug reservado. Escolha outro.");
      return null;
    }

    setSlugStatus("checking");
    setSlugMessage("Validando disponibilidade...");
    const isAvailable = await SuperAdminService.isClientSlugAvailable(normalizedSlug);
    if (!isAvailable) {
      setSlugStatus("invalid");
      setSlugMessage("Slug já está em uso. Escolha outro.");
      return null;
    }

    setSlugStatus("available");
    setSlugMessage("Slug disponível.");
    return normalizedSlug;
  };

  const handleCreateTenant = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedTenantName = tenantName.trim();
    if (!normalizedTenantName) {
      toast.error("Nome do tenant é obrigatório.");
      return;
    }

    const assignmentsWithEmail = assignments.filter(
      (assignment) => normalizeEmail(assignment.userEmail).length > 0
    );

    if (assignmentsWithEmail.length === 0) {
      toast.error("Informe ao menos um usuário administrador.");
      return;
    }

    for (const assignment of assignmentsWithEmail) {
      const normalizedEmail = normalizeEmail(assignment.userEmail);
      if (!emailRegex.test(normalizedEmail)) {
        toast.error(`Email inválido: ${assignment.userEmail}`);
        return;
      }
    }

    const duplicatedEmail = assignmentsWithEmail
      .map((assignment) => normalizeEmail(assignment.userEmail))
      .find(
        (email, index, list) =>
          list.indexOf(email) !== index
      );

    if (duplicatedEmail) {
      toast.error(`Email duplicado na lista: ${duplicatedEmail}`);
      return;
    }

    if (assignmentsWithEmail.some((assignment) => assignment.emailLookupState === "checking")) {
      toast.error("Aguarde a validação dos emails antes de criar o tenant.");
      return;
    }

    const validatedSlug = await validateSlug();
    if (!validatedSlug) {
      toast.error("Ajuste o slug do tenant para continuar.");
      return;
    }

    setIsSubmitting(true);

    try {
      const emailExistsMap = new Map<string, boolean>();

      for (const assignment of assignmentsWithEmail) {
        const emailExists = await lookupUserByEmail(assignment.id, assignment.userEmail);
        if (emailExists === null) {
          throw new Error(`Não foi possível validar o email ${assignment.userEmail}.`);
        }
        emailExistsMap.set(assignment.id, emailExists);

        if (!emailExists) {
          if (!assignment.userDisplayName || assignment.userDisplayName.trim().length === 0) {
            throw new Error(`Nome do usuário é obrigatório para ${assignment.userEmail}.`);
          }

          if (!assignment.password || assignment.password.trim().length === 0) {
            throw new Error(`Senha é obrigatória para ${assignment.userEmail}.`);
          }
        }
      }

      const payload: InitialTenantUserInput[] = assignmentsWithEmail.map((assignment) => {
        const emailExists = emailExistsMap.get(assignment.id) === true;
        return {
          userDisplayName: emailExists ? undefined : assignment.userDisplayName?.trim(),
          userEmail: normalizeEmail(assignment.userEmail),
          userRole: "admin",
          password: emailExists ? undefined : assignment.password?.trim(),
        };
      });

      const result = await SuperAdminService.createTenantWithInitialUsers(
        normalizedTenantName,
        payload,
        validatedSlug
      );

      toast.success(
        `Tenant ${result.clientName} (${result.clientSlug}) criado (${result.assignments.length} usuário(s) provisionado(s)).`
      );

      resetForm();
      await loadTenants();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao criar tenant.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTenantUpdated = async (updatedTenant: TenantOverview, previousSlug: string) => {
    setTenants((current) =>
      current.map((tenant) =>
        tenant.clientId === updatedTenant.clientId ? updatedTenant : tenant
      )
    );
    setTenantBeingEdited(updatedTenant);

    const currentTenantSlug = extractTenantSlugFromPathname(location.pathname);
    if (
      currentTenantSlug &&
      normalizeTenantSlug(currentTenantSlug) === normalizeTenantSlug(previousSlug) &&
      normalizeTenantSlug(previousSlug) !== normalizeTenantSlug(updatedTenant.clientSlug)
    ) {
      navigate(buildTenantPath("/super-admin", updatedTenant.clientSlug), { replace: true });
    }

    await loadTenants();
  };

  return (
    <div className="revahub-app min-h-screen bg-neutral-50 py-20">
      <div className="container mx-auto px-4 space-y-8">
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-primary-600" />
            <h1 className="text-2xl font-display font-bold text-neutral-900">Painel Super Admin</h1>
          </div>
          <p className="text-neutral-600">
            Crie tenants e defina os papéis iniciais dos usuários responsáveis por cada clínica.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <section className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Tenants cadastrados</h2>

            {isLoadingTenants ? (
              <div className="flex items-center justify-center py-10">
                <div className="h-8 w-8 rounded-full border-2 border-primary-600 border-t-transparent animate-spin" />
              </div>
            ) : tenants.length === 0 ? (
              <p className="text-neutral-600">Nenhum tenant cadastrado até o momento.</p>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
                {tenants.map((tenant) => (
                  <div
                    key={tenant.clientId}
                    className="rounded-lg border border-neutral-200 p-4 flex items-start justify-between gap-3"
                  >
                    <div>
                      <p className="font-medium text-neutral-900">{tenant.clientName}</p>
                      <p className="text-sm text-neutral-500">Slug: {tenant.clientSlug || "-"}</p>
                      <p className="text-sm text-neutral-500">ID: {tenant.clientId}</p>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <p className="text-sm text-neutral-500">
                        {new Date(tenant.clientCreatedAt).toLocaleDateString("pt-BR")}
                      </p>
                      <button
                        type="button"
                        onClick={() => setTenantBeingEdited(tenant)}
                        className="inline-flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
                      >
                        <Pencil className="h-4 w-4" />
                        Editar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-neutral-100 p-6">
            <h2 className="text-lg font-semibold text-neutral-900 mb-1">Criar novo tenant</h2>
            <p className="text-sm text-neutral-600 mb-6">Os usuários iniciais serão criados apenas como administradores.</p>

            <form onSubmit={handleCreateTenant} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nome do tenant
                </label>
                <input
                  value={tenantName}
                  onChange={(event) => {
                    setTenantName(event.target.value);
                  }}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ex: Clínica Central"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Slug do tenant
                </label>
                <input
                  value={tenantSlug}
                  onChange={(event) => {
                    setIsSlugTouched(true);
                    setSlugStatus("idle");
                    setSlugMessage("");
                    setTenantSlug(normalizeTenantSlug(event.target.value));
                  }}
                  onBlur={() => {
                    void validateSlug();
                  }}
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ex-clinica-central"
                  required
                />
                <p className="mt-1 text-xs text-neutral-500">
                  Apenas letras minúsculas, números e hífen.
                </p>
                {slugStatus !== "idle" && slugMessage && (
                  <p
                    className={`mt-1 text-xs ${
                      slugStatus === "available" ? "text-emerald-700" : slugStatus === "checking" ? "text-neutral-500" : "text-red-600"
                    }`}
                  >
                    {slugMessage}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-neutral-700">Usuários iniciais</p>
                  <button
                    type="button"
                    onClick={addAssignment}
                    className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar usuário
                  </button>
                </div>

                {assignments.map((assignment, index) => (
                  <div key={assignment.id} className="rounded-lg border border-neutral-200 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-neutral-800 inline-flex items-center gap-2">
                        <Users className="w-4 h-4 text-neutral-500" />
                        Usuário #{index + 1}
                      </p>
                      {assignments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAssignment(assignment.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Remover usuário"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        value={assignment.userDisplayName}
                        onChange={(event) =>
                          updateAssignment(assignment.id, (row) => ({
                            ...row,
                            userDisplayName: event.target.value,
                          }))
                        }
                        readOnly={assignment.emailLookupState === "exists"}
                        required={assignment.emailLookupState === "not_found"}
                        className={`rounded-md border px-3 py-2 focus:ring-primary-500 focus:border-primary-500 ${
                          assignment.emailLookupState === "exists"
                            ? "border-neutral-200 bg-neutral-100 text-neutral-700"
                            : "border-neutral-300"
                        }`}
                        placeholder={
                          assignment.emailLookupState === "exists"
                            ? "Nome carregado automaticamente"
                            : "Nome do usuário"
                        }
                      />
                      <input
                        type="email"
                        value={assignment.userEmail}
                        onChange={(event) =>
                          scheduleEmailLookup(assignment.id, event.target.value)
                        }
                        onBlur={() => {
                          const normalizedEmail = normalizeEmail(assignment.userEmail);
                          if (normalizedEmail && emailRegex.test(normalizedEmail)) {
                            clearEmailLookupTimer(assignment.id);
                            void lookupUserByEmail(assignment.id, normalizedEmail);
                          }
                        }}
                        className="rounded-md border border-neutral-300 px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="email@empresa.com"
                        required
                      />
                    </div>

                    <div className="min-h-5">
                      {assignment.emailLookupState === "checking" && (
                        <p className="text-xs text-neutral-500">Verificando email...</p>
                      )}
                      {assignment.emailLookupState === "exists" && (
                        <p className="text-xs text-emerald-700">
                          Email já cadastrado. Nome preenchido automaticamente e senha não é necessária.
                        </p>
                      )}
                      {assignment.emailLookupState === "not_found" && (
                        <p className="text-xs text-amber-700">
                          Email não encontrado. Nome do usuário e senha são obrigatórios.
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        value="Administrador"
                        readOnly
                        className="rounded-md border border-neutral-200 bg-neutral-100 px-3 py-2 text-neutral-700"
                      />

                      {assignment.emailLookupState !== "exists" && (
                        <input
                          type="password"
                          value={assignment.password}
                          onChange={(event) =>
                            updateAssignment(assignment.id, (row) => ({
                              ...row,
                              password: event.target.value,
                            }))
                          }
                          required={assignment.emailLookupState === "not_found"}
                          className="rounded-md border border-neutral-300 px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Senha (obrigatória para novo usuário)"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-primary-600 text-white py-2.5 font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Criando tenant..." : "Criar tenant e provisionar usuários"}
              </button>
            </form>
          </section>
        </div>
      </div>

      {tenantBeingEdited && (
        <EditTenantModal
          tenant={tenantBeingEdited}
          onClose={() => setTenantBeingEdited(null)}
          onTenantUpdated={handleTenantUpdated}
        />
      )}
    </div>
  );
};

export default SuperAdminDashboard;
